'use client';

/**
 * TierBenefitsMap - Compact tier cards matching target design exactly
 * Transparent backgrounds, subtle borders, NO border highlight on current tier
 */

import React, { useState, useRef, useEffect } from 'react';
import { Lock, CheckCircle2, X } from 'lucide-react';
import type { ReferralTier } from '../types';
import { cn } from '@/lib/utils';

interface TierBenefitsMapProps {
  tiers: ReferralTier[];
  currentTierName: string;
}

export function TierBenefitsMap({ tiers, currentTierName }: TierBenefitsMapProps) {
  const [expandedTier, setExpandedTier] = useState<string | null>(null);
  // eslint-disable-next-line @eslint-react/naming-convention-ref-name -- cardRefs is a map-of-refs pattern for multi-element tracking; the "Ref" suffix is intentional plural naming
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const currentTierIndex = tiers.findIndex((t) => t.name === currentTierName);

  const handleTierClick = (tierName: string) => {
    setExpandedTier(expandedTier === tierName ? null : tierName);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        expandedTier &&
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setExpandedTier(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [expandedTier]);

  return (
    <div className="space-y-4" ref={containerRef}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">
          Tier Benefits Map
        </h3>
        <div className="flex items-center gap-5 text-[10px] text-zinc-600">
          <span className="flex items-center gap-2">
            <Lock className="h-3 w-3" />
            Locked
          </span>
          <span className="flex items-center gap-2">
            <CheckCircle2 className="h-3 w-3 text-emerald-500" />
            Unlocked
          </span>
        </div>
      </div>

      {/* Grid of Cards - Bubble Style */}
      <div className="relative grid grid-cols-2 lg:grid-cols-4 gap-4">
        {tiers.map((tier, index) => {
          const isUnlocked = index <= currentTierIndex;
          const isCurrent = tier.name === currentTierName;
          const isExpanded = expandedTier === tier.name;

          return (
            <div
              key={tier.id}
              ref={(el) => {
                if (el) cardRefs.current.set(tier.name, el);
              }}
              className="relative"
            >
              {/* Card - Bubble Style */}
              <div
                className={cn(
                  'rounded-[1.5rem] bg-white/60 dark:bg-zinc-900/40 backdrop-blur-xl border border-zinc-200 dark:border-zinc-800 p-4',
                  'hover:border-zinc-300 dark:hover:border-zinc-700 transition-all duration-200 cursor-pointer',
                  isExpanded && 'opacity-40'
                )}
                onClick={() => handleTierClick(tier.name)}
              >
                {/* Locked indicator */}
                <div className="absolute top-3 right-3">
                  {isUnlocked ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <Lock className="h-4 w-4 text-zinc-600" />
                  )}
                </div>

                {/* Commission badge + Name */}
                <div className="flex items-center gap-2 mb-3 pr-6">
                  <span
                    className={cn(
                      'text-[10px] font-black px-2 py-0.5 rounded-md',
                      isUnlocked
                        ? 'bg-amber-500/10 text-amber-500'
                        : 'bg-zinc-200 dark:bg-zinc-800 text-zinc-500'
                    )}
                  >
                    {tier.commission_percent}%
                  </span>
                  <span className="text-[11px] uppercase tracking-wider text-zinc-600 dark:text-zinc-400 font-semibold">
                    {tier.display_name.split(' (')[0]}
                  </span>
                </div>

                {/* Referrals range */}
                <p className="text-[10px] text-zinc-500 dark:text-zinc-600 mb-3">
                  {tier.min_referrals} - {tier.max_referrals ?? '∞'} Referrals
                </p>

                {/* Benefits - Show first 2 */}
                {/* eslint-disable @eslint-react/no-array-index-key -- array of benefit strings with no stable id; read-only display list, never reordered */}
                <ul className="space-y-1.5 mb-3">
                  {tier.benefits.slice(0, 2).map((benefit, benefitIndex) => (
                    <li
                      key={benefitIndex}
                      className={cn(
                        'text-[10px] flex items-start gap-2',
                        isUnlocked
                          ? 'text-zinc-600 dark:text-zinc-400'
                          : 'text-zinc-500 dark:text-zinc-600'
                      )}
                    >
                      <span className="text-amber-500 mt-0.5">•</span>
                      <span className="leading-tight truncate">{benefit}</span>
                    </li>
                  ))}
                </ul>
                {/* eslint-enable @eslint-react/no-array-index-key */}

                {/* Current badge - ONLY indicator for current tier */}
                {isCurrent && (
                  <span className="inline-block text-[9px] font-semibold px-2 py-1 rounded-md bg-amber-500/10 text-amber-500">
                    ✓ CURRENT
                  </span>
                )}
              </div>

              {/* Expanded Popup - Bubble Style */}
              {isExpanded && (
                <div className="absolute z-[10002] top-0 left-0 w-56 animate-in fade-in zoom-in-95 duration-200">
                  <div className="rounded-[1.5rem] bg-white/95 dark:bg-zinc-900/95 backdrop-blur-xl border border-zinc-200 dark:border-zinc-700 shadow-2xl shadow-black/20 dark:shadow-black/60">
                    {/* Close */}
                    <button
                      type="button"
                      aria-label="Close tier details"
                      className="absolute top-3 right-3 p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedTier(null);
                      }}
                    >
                      <X className="h-4 w-4 text-zinc-500" />
                    </button>

                    <div className="p-4">
                      {/* Header */}
                      <div className="flex items-center gap-2 mb-4 pr-6">
                        {isUnlocked ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <Lock className="h-4 w-4 text-zinc-600" />
                        )}
                        <span className="text-sm font-black tracking-tight text-zinc-900 dark:text-white">
                          {tier.display_name.split(' (')[0]}
                        </span>
                      </div>

                      {/* Commission */}
                      <div className="mb-4 pb-4 border-b border-zinc-200 dark:border-zinc-800">
                        <span className="text-[10px] text-zinc-500 uppercase tracking-widest">
                          Commission Rate
                        </span>
                        <p className="text-2xl font-black tracking-tighter text-amber-500 mt-1">
                          {tier.commission_percent}%
                        </p>
                      </div>

                      {/* All Benefits */}
                      <div>
                        <span className="text-[10px] text-zinc-500 uppercase tracking-widest">
                          Benefits
                        </span>
                        {/* eslint-disable @eslint-react/no-array-index-key -- array of benefit strings with no stable id; read-only display list, never reordered */}
                        <ul className="mt-2 space-y-2">
                          {tier.benefits.map((benefit, idx) => (
                            <li
                              key={idx}
                              className={cn(
                                'text-[11px] flex items-start gap-2',
                                isUnlocked ? 'text-zinc-700 dark:text-zinc-300' : 'text-zinc-500'
                              )}
                            >
                              <CheckCircle2
                                className={cn(
                                  'h-3 w-3 mt-0.5 flex-shrink-0',
                                  isUnlocked ? 'text-emerald-500' : 'text-zinc-700'
                                )}
                              />
                              <span className="leading-tight">{benefit}</span>
                            </li>
                          ))}
                        </ul>
                        {/* eslint-enable @eslint-react/no-array-index-key */}
                      </div>

                      {/* Status */}
                      <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-800">
                        {isUnlocked ? (
                          <span className="text-[10px] font-semibold px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400">
                            UNLOCKED
                          </span>
                        ) : (
                          <span className="text-[10px] font-semibold px-3 py-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-500">
                            {tier.min_referrals} referrals to unlock
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
